import React from "react";
import { AlertTriangle, RefreshCw, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAccess, DEFAULT_PLAN } from "@/context/AccessContext";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export function RenewBanner() {
  const { active, kind, status, provider, cancel_at_period_end, daysLeft, expires_at, checkingOut, subscribe, openPortal, trial_days } = useAccess();
  const [busy, setBusy] = React.useState(false);
  if (!active) return null;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); } finally { setBusy(false); }
  };
  const paypal = provider === "paypal";

  let text, cta, onClick, testid;
  if (kind === "pass" && daysLeft <= 3) {
    text = <>Your one-time pass expires in <span className="text-oracle font-semibold">{daysLeft} day{daysLeft === 1 ? "" : "s"}</span>{expires_at && <span className="text-parchment/50"> · {fmt(expires_at)}</span>}. Subscribe to keep the Oracle — {trial_days}-day free trial, from $4.99/mo.</>;
    cta = "Start free trial"; onClick = () => run(() => subscribe(DEFAULT_PLAN)); testid = "renew-now-btn";
  } else if (kind === "subscription" && status === "past_due") {
    text = paypal
      ? <>Your last PayPal payment <span className="text-oracle font-semibold">failed</span>. Update your funding source at paypal.com to keep access — PayPal will retry automatically.</>
      : <>Your last payment <span className="text-oracle font-semibold">failed</span>. Update your card to keep access — we'll retry automatically.</>;
    cta = paypal ? "Open PayPal" : "Update card"; onClick = paypal ? () => window.open("https://www.paypal.com/myaccount/autopay/", "_blank", "noopener") : () => run(openPortal); testid = "fix-payment-btn";
  } else if (kind === "subscription" && cancel_at_period_end && daysLeft <= 7) {
    text = <>Your subscription is set to end {expires_at && <span className="text-oracle font-semibold">{fmt(expires_at)}</span>}. {paypal ? "You can start a new plan once it ends." : "Resume anytime to keep your access."}</>;
    if (paypal) { cta = null; } else { cta = "Resume subscription"; onClick = () => run(openPortal); testid = "resume-sub-btn"; }
  } else {
    return null;
  }

  return (
    <div data-testid="renew-banner" className="bg-oracle/10 border-b border-oracle/30">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 font-body text-sm text-parchment">
          <AlertTriangle className="w-4 h-4 text-oracle shrink-0" strokeWidth={1.5} />
          <span data-testid="renew-banner-text">{text}</span>
        </div>
        {cta && (
          <Button data-testid={testid} onClick={onClick} disabled={busy || checkingOut} className="bg-oracle text-ink hover:bg-[#FFC033] label-caps shrink-0">
            {busy || checkingOut ? <><RefreshCw className="w-3 h-3 mr-2 animate-spin" /> Opening…</> : <><CreditCard className="w-3 h-3 mr-2" strokeWidth={1.5} /> {cta}</>}
          </Button>
        )}
      </div>
    </div>
  );
}
